import { createContext, type ReactNode, useContext, useMemo } from "react";
import { CLOCK_CADENCE, useWallClock } from "@/providers/wall-clock";
import { useDataViewerStore } from "../../data-viewer-provider";
import type { PreparedSeries } from "../prepare-series";
import { buildReadout, type Readout } from "./readout";

type PanelReadoutValue = { readout: Readout; nowMs: number };

const PanelReadoutContext = createContext<PanelReadoutValue | null>(null);

/** Builds the readout once per panel. Hover time is read here, so a mousemove re-renders the readout's consumers, not the chart. */
export function PanelReadoutProvider({ series, children }: { series: PreparedSeries[]; children: ReactNode }) {
    const hoveredTimeMs = useDataViewerStore((state) => state.timestamp);
    const snapToReading = useDataViewerStore((state) => state.hoverTarget !== "band");
    const isLiveRange = useDataViewerStore((state) => state.preset !== null);
    const nowMs = useWallClock(CLOCK_CADENCE.MINUTES_1);

    const value = useMemo(
        () => ({ readout: buildReadout({ series, hoveredTimeMs, snapToReading, isLiveRange, nowMs }), nowMs }),
        [series, hoveredTimeMs, snapToReading, isLiveRange, nowMs],
    );
    return <PanelReadoutContext.Provider value={value}>{children}</PanelReadoutContext.Provider>;
}

export function usePanelReadout(): PanelReadoutValue {
    const value = useContext(PanelReadoutContext);
    if (!value) throw new Error("usePanelReadout must be used inside PanelReadoutProvider");
    return value;
}
